import { useState } from "react";
import { motion } from "framer-motion";
import { useAuth, API } from "../App";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  DollarSign,
  Search,
  TrendingUp,
  BarChart3,
  Loader2,
  MapPin,
  Briefcase,
  Plus,
  ChevronDown,
  Users,
  Award,
  MessageSquare,
  Sparkles,
  Shield,
  CheckCircle,
  XCircle
} from "lucide-react";

const SalaryResearch = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("research");
  const [searchTitle, setSearchTitle] = useState("");
  const [searchLocation, setSearchLocation] = useState("");
  const [searchCurrency, setSearchCurrency] = useState("USD");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [showContribute, setShowContribute] = useState(false);
  const [contributing, setContributing] = useState(false);
  const [contributeForm, setContributeForm] = useState({
    job_title: "", company_name: "", location: "",
    experience_years: 3, salary_amount: "", salary_currency: "USD",
    salary_period: "yearly", employment_type: "full-time"
  });

  // Negotiation state
  const [negoForm, setNegoForm] = useState({
    job_title: "", company_name: "", offered_salary: "",
    offered_currency: "USD", country: "", state: "", city: "",
    experience_years: 3, current_salary: ""
  });
  const [negoLoading, setNegoLoading] = useState(false);
  const [negoResult, setNegoResult] = useState(null);

  const handleSearch = async () => {
    if (!searchTitle.trim()) return toast.error("Enter a job title");
    setLoading(true);
    setData(null);
    try {
      const token = localStorage.getItem("session_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const params = new URLSearchParams({ job_title: searchTitle, currency: searchCurrency });
      if (searchLocation) params.append("location", searchLocation);

      const res = await fetch(`${API}/salary/research?${params}`, { credentials: "include", headers });
      if (res.ok) {
        setData(await res.json());
      } else {
        const err = await res.json();
        toast.error(err.detail || "Search failed");
      }
    } catch {
      toast.error("Failed to search");
    } finally {
      setLoading(false);
    }
  };

  const handleContribute = async () => {
    if (!contributeForm.job_title.trim()) return toast.error("Job title required");
    if (!contributeForm.salary_amount || contributeForm.salary_amount <= 0) return toast.error("Valid salary required");
    setContributing(true);
    try {
      const token = localStorage.getItem("session_token");
      const headers = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const res = await fetch(`${API}/salary/report`, {
        method: "POST", credentials: "include", headers,
        body: JSON.stringify({ ...contributeForm, salary_amount: parseFloat(contributeForm.salary_amount) }),
      });
      if (res.ok) {
        toast.success("Thank you! Your salary data has been added anonymously.");
        setShowContribute(false);
        setContributeForm({ ...contributeForm, salary_amount: "" });
        if (searchTitle) handleSearch(); // refresh
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to submit");
      }
    } catch {
      toast.error("Submission failed");
    } finally {
      setContributing(false);
    }
  };

  const handleNegotiate = async () => {
    if (!negoForm.job_title.trim()) return toast.error("Job title required");
    if (!negoForm.offered_salary || parseFloat(negoForm.offered_salary) <= 0) return toast.error("Enter the offered salary");
    setNegoLoading(true);
    setNegoResult(null);
    try {
      const token = localStorage.getItem("session_token");
      const headers = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;
      const res = await fetch(`${API}/salary/negotiate`, {
        method: "POST", credentials: "include", headers,
        body: JSON.stringify({
          job_title: negoForm.job_title,
          company_name: negoForm.company_name,
          offered_salary: parseFloat(negoForm.offered_salary),
          offered_currency: negoForm.offered_currency,
          location: [negoForm.city, negoForm.state, negoForm.country].filter(Boolean).join(", "),
          experience_years: negoForm.experience_years,
          current_salary: negoForm.current_salary ? parseFloat(negoForm.current_salary) : null,
        }),
      });
      if (res.ok) {
        setNegoResult(await res.json());
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to get advice");
      }
    } catch {
      toast.error("Failed to get negotiation advice");
    } finally {
      setNegoLoading(false);
    }
  };

  const formatSalary = (amount, currency = "USD") => {
    if (!amount) return "N/A";
    return `${currency} ${Number(amount).toLocaleString()}`;
  };

  const inputClass = "w-full bg-obsidian border border-[#333] rounded px-4 py-2.5 text-sm text-white placeholder-gray-500 focus:border-crimson focus:outline-none";

  return (
    <div className="min-h-screen bg-obsidian text-white">
      <Sidebar />
      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen">
        <div className="max-w-5xl mx-auto p-4 sm:p-6 lg:p-8 space-y-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-3" data-testid="salary-research-title">
                <DollarSign className="w-7 h-7 text-crimson" />
                Salary Research
              </h1>
              <p className="text-gray-400 mt-1 text-sm">Research salaries by role and location — powered by community data</p>
            </div>
            {user && (
              <Button onClick={() => setShowContribute(!showContribute)} variant="outline" className="border-[#333] text-gray-300 hover:bg-white/5" data-testid="contribute-toggle">
                <Plus className="w-4 h-4 mr-2" /> Contribute Salary
              </Button>
            )}
          </div>

          {/* Tabs */}
          <div className="flex gap-1 bg-obsidian-light border border-[#222] rounded-lg p-1 w-fit">
            {[
              { id: "research", label: "Salary Research", icon: Search },
              { id: "negotiate", label: "Negotiate Offer", icon: Sparkles },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded text-sm font-medium transition-colors ${
                  activeTab === tab.id ? "bg-crimson text-white" : "text-gray-400 hover:text-white hover:bg-white/5"
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === "negotiate" && (
            <div className="space-y-6">
              <div className="bg-obsidian-light border border-[#222] rounded-lg p-5 space-y-4">
                <h3 className="font-semibold flex items-center gap-2"><Sparkles className="w-5 h-5 text-crimson" /> AI Negotiation Advisor</h3>
                <p className="text-sm text-gray-400">Get AI-powered advice on how to negotiate your salary offer.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Job Title *</label>
                    <input type="text" placeholder="e.g., Software Engineer" value={negoForm.job_title} onChange={(e) => setNegoForm({ ...negoForm, job_title: e.target.value })} className={inputClass} data-testid="nego-title" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Company Name</label>
                    <input type="text" placeholder="e.g., Google" value={negoForm.company_name} onChange={(e) => setNegoForm({ ...negoForm, company_name: e.target.value })} className={inputClass} data-testid="nego-company" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Offered Salary *</label>
                    <div className="flex">
                      <select value={negoForm.offered_currency} onChange={(e) => setNegoForm({ ...negoForm, offered_currency: e.target.value })} className="bg-obsidian border border-[#333] border-r-0 rounded-l px-3 py-2.5 text-sm text-white focus:border-crimson focus:outline-none" data-testid="nego-currency">
                        {["USD", "EUR", "GBP", "CAD", "AUD", "INR", "NGN", "AED", "SGD"].map((c) => <option key={c}>{c}</option>)}
                      </select>
                      <input type="number" placeholder="e.g., 85000" value={negoForm.offered_salary} onChange={(e) => setNegoForm({ ...negoForm, offered_salary: e.target.value })} className="w-full bg-obsidian border border-[#333] rounded-r px-4 py-2.5 text-sm text-white placeholder-gray-500 focus:border-crimson focus:outline-none" data-testid="nego-salary" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Current Salary (optional)</label>
                    <input type="number" placeholder="e.g., 70000" value={negoForm.current_salary} onChange={(e) => setNegoForm({ ...negoForm, current_salary: e.target.value })} className={inputClass} data-testid="nego-current" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Country *</label>
                    <input type="text" placeholder="e.g., United States" value={negoForm.country} onChange={(e) => setNegoForm({ ...negoForm, country: e.target.value })} className={inputClass} data-testid="nego-country" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">State / Province</label>
                    <input type="text" placeholder="e.g., California" value={negoForm.state} onChange={(e) => setNegoForm({ ...negoForm, state: e.target.value })} className={inputClass} data-testid="nego-state" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">City</label>
                    <input type="text" placeholder="e.g., San Francisco" value={negoForm.city} onChange={(e) => setNegoForm({ ...negoForm, city: e.target.value })} className={inputClass} data-testid="nego-city" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Years of Experience</label>
                    <input type="number" placeholder="e.g., 5" value={negoForm.experience_years} onChange={(e) => setNegoForm({ ...negoForm, experience_years: parseInt(e.target.value) || 0 })} min={0} className={inputClass} data-testid="nego-exp" />
                  </div>
                </div>
                <Button onClick={handleNegotiate} disabled={negoLoading} className="w-full bg-crimson hover:bg-crimson/90 text-white" data-testid="negotiate-btn">
                  {negoLoading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Analyzing...</> : <><Sparkles className="w-4 h-4 mr-2" /> Get Negotiation Advice</>}
                </Button>
              </div>

              {negoResult && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
                  {/* Assessment */}
                  <div className={`bg-obsidian-light border rounded-lg p-5 ${
                    negoResult.advice?.assessment === "below_market" ? "border-rose-500/30" :
                    negoResult.advice?.assessment === "above_market" ? "border-emerald-500/30" :
                    "border-amber-500/30"
                  }`}>
                    <div className="flex items-center gap-2 mb-2">
                      {negoResult.advice?.assessment === "below_market" ? <XCircle className="w-5 h-5 text-rose-400" /> :
                       negoResult.advice?.assessment === "above_market" ? <CheckCircle className="w-5 h-5 text-emerald-400" /> :
                       <Shield className="w-5 h-5 text-amber-400" />}
                      <h3 className="font-semibold capitalize">{negoResult.advice?.assessment?.replace("_", " ")}</h3>
                    </div>
                    <p className="text-sm text-gray-300">{negoResult.advice?.assessment_summary}</p>
                  </div>

                  {/* Counter offer range */}
                  <div className="bg-obsidian-light border border-[#222] rounded-lg p-5">
                    <h3 className="font-semibold text-sm mb-3">Suggested Counter Range</h3>
                    <div className="grid grid-cols-3 gap-3 text-center">
                      <div className="bg-obsidian border border-[#333] rounded p-3">
                        <p className="text-xs text-gray-500">Low</p>
                        <p className="text-lg font-bold text-amber-400">{formatSalary(negoResult.advice?.counter_range_low, negoResult.offer?.currency)}</p>
                      </div>
                      <div className="bg-obsidian border border-crimson/30 rounded p-3">
                        <p className="text-xs text-gray-500">Suggested</p>
                        <p className="text-lg font-bold text-crimson">{formatSalary(negoResult.advice?.suggested_counter, negoResult.offer?.currency)}</p>
                      </div>
                      <div className="bg-obsidian border border-[#333] rounded p-3">
                        <p className="text-xs text-gray-500">High</p>
                        <p className="text-lg font-bold text-emerald-400">{formatSalary(negoResult.advice?.counter_range_high, negoResult.offer?.currency)}</p>
                      </div>
                    </div>
                  </div>

                  {/* Script */}
                  {negoResult.advice?.negotiation_script && (
                    <div className="bg-crimson/5 border border-crimson/20 rounded-lg p-5">
                      <h3 className="font-semibold text-sm mb-2 flex items-center gap-2"><MessageSquare className="w-4 h-4 text-crimson" /> What to Say</h3>
                      <p className="text-sm text-gray-300 italic">"{negoResult.advice.negotiation_script}"</p>
                    </div>
                  )}

                  {/* Talking points & benefits */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {negoResult.advice?.talking_points?.length > 0 && (
                      <div className="bg-obsidian-light border border-[#222] rounded-lg p-5">
                        <h4 className="text-xs text-emerald-400 font-medium mb-2">Talking Points</h4>
                        <ul className="space-y-1.5">
                          {negoResult.advice.talking_points.map((p, i) => (
                            <li key={i} className="text-sm text-gray-300 flex items-start gap-2"><CheckCircle className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0 mt-0.5" />{p}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {negoResult.advice?.benefits_to_negotiate?.length > 0 && (
                      <div className="bg-obsidian-light border border-[#222] rounded-lg p-5">
                        <h4 className="text-xs text-blue-400 font-medium mb-2">Benefits to Negotiate</h4>
                        <ul className="space-y-1.5">
                          {negoResult.advice.benefits_to_negotiate.map((b, i) => (
                            <li key={i} className="text-sm text-gray-300 flex items-start gap-2"><Plus className="w-3.5 h-3.5 text-blue-400 flex-shrink-0 mt-0.5" />{b}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Mistakes */}
                  {negoResult.advice?.mistakes_to_avoid?.length > 0 && (
                    <div className="bg-obsidian-light border border-[#222] rounded-lg p-5">
                      <h4 className="text-xs text-rose-400 font-medium mb-2">Mistakes to Avoid</h4>
                      <ul className="space-y-1.5">
                        {negoResult.advice.mistakes_to_avoid.map((m, i) => (
                          <li key={i} className="text-sm text-gray-300 flex items-start gap-2"><XCircle className="w-3.5 h-3.5 text-rose-400 flex-shrink-0 mt-0.5" />{m}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </motion.div>
              )}
            </div>
          )}

          {activeTab === "research" && (
          <>
          {/* Contribute Form */}
          {showContribute && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="bg-obsidian-light border border-[#222] rounded-lg p-5 space-y-4">
              <h3 className="font-semibold">Share Your Salary (Anonymous)</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input type="text" placeholder="Job Title *" value={contributeForm.job_title} onChange={(e) => setContributeForm({ ...contributeForm, job_title: e.target.value })} className={inputClass} data-testid="contrib-title" />
                <input type="text" placeholder="Company (optional)" value={contributeForm.company_name} onChange={(e) => setContributeForm({ ...contributeForm, company_name: e.target.value })} className={inputClass} data-testid="contrib-company" />
                <input type="text" placeholder="Location" value={contributeForm.location} onChange={(e) => setContributeForm({ ...contributeForm, location: e.target.value })} className={inputClass} data-testid="contrib-location" />
                <input type="number" placeholder="Years of Experience" value={contributeForm.experience_years} onChange={(e) => setContributeForm({ ...contributeForm, experience_years: parseInt(e.target.value) || 0 })} min={0} className={inputClass} data-testid="contrib-exp" />
                <div className="flex gap-2">
                  <select value={contributeForm.salary_currency} onChange={(e) => setContributeForm({ ...contributeForm, salary_currency: e.target.value })} className={`${inputClass} w-24`}>
                    {["USD", "EUR", "GBP", "CAD", "AUD", "INR", "NGN", "AED", "SGD"].map((c) => <option key={c}>{c}</option>)}
                  </select>
                  <input type="number" placeholder="Annual Salary *" value={contributeForm.salary_amount} onChange={(e) => setContributeForm({ ...contributeForm, salary_amount: e.target.value })} className={`${inputClass} flex-1`} data-testid="contrib-salary" />
                </div>
                <select value={contributeForm.salary_period} onChange={(e) => setContributeForm({ ...contributeForm, salary_period: e.target.value })} className={inputClass}>
                  <option value="yearly">Per Year</option>
                  <option value="monthly">Per Month</option>
                  <option value="hourly">Per Hour</option>
                </select>
              </div>
              <div className="flex gap-2">
                <Button onClick={() => setShowContribute(false)} variant="outline" className="border-[#333] text-gray-300">Cancel</Button>
                <Button onClick={handleContribute} disabled={contributing} className="bg-crimson hover:bg-crimson/90 text-white" data-testid="submit-salary-btn">
                  {contributing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Submit
                </Button>
              </div>
            </motion.div>
          )}

          {/* Search */}
          <div className="bg-obsidian-light border border-[#222] rounded-lg p-5">
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
              <div className="sm:col-span-2">
                <label className="block text-xs text-gray-400 mb-1">Job Title</label>
                <input type="text" value={searchTitle} onChange={(e) => setSearchTitle(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleSearch()} placeholder="e.g., Software Engineer" className={inputClass} data-testid="search-title" />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Location</label>
                <input type="text" value={searchLocation} onChange={(e) => setSearchLocation(e.target.value)} placeholder="e.g., Lagos, London" className={inputClass} data-testid="search-location" />
              </div>
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <label className="block text-xs text-gray-400 mb-1">Currency</label>
                  <select value={searchCurrency} onChange={(e) => setSearchCurrency(e.target.value)} className={inputClass} data-testid="search-currency">
                    {["USD", "EUR", "GBP", "CAD", "AUD", "INR", "NGN", "AED", "SGD"].map((c) => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <Button onClick={handleSearch} disabled={loading} className="bg-crimson hover:bg-crimson/90 text-white" data-testid="salary-search-btn">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </div>

          {/* Results */}
          {loading && (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-crimson" />
            </div>
          )}

          {data && !loading && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
              {/* Summary Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: "Minimum", value: formatSalary(data.salary_min, data.currency), color: "text-blue-400" },
                  { label: "Average", value: formatSalary(data.salary_avg, data.currency), color: "text-amber-400" },
                  { label: "Median", value: formatSalary(data.salary_median, data.currency), color: "text-emerald-400" },
                  { label: "Maximum", value: formatSalary(data.salary_max, data.currency), color: "text-crimson" },
                ].map((s) => (
                  <div key={s.label} className="bg-obsidian-light border border-[#222] rounded p-4" data-testid={`stat-${s.label.toLowerCase()}`}>
                    <p className="text-xs text-gray-500 mb-1">{s.label}</p>
                    <p className={`text-lg font-bold ${s.color}`}>{s.value}</p>
                  </div>
                ))}
              </div>

              <div className="bg-obsidian-light border border-[#222] rounded-lg p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-sm flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 text-crimson" />
                    {data.job_title} — {data.location}
                  </h3>
                  <span className="text-xs text-gray-500">
                    {data.sample_size} data points ({data.source === "community" ? "Community" : data.source === "job_postings" ? "Job Postings" : "No data"})
                  </span>
                </div>

                {data.sample_size === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <DollarSign className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">No salary data found for this search.</p>
                    <p className="text-xs mt-1">Be the first to contribute!</p>
                  </div>
                ) : (
                  <>
                    {/* Salary range bar */}
                    <div className="py-4">
                      <div className="relative h-8 bg-[#222] rounded-full overflow-hidden">
                        <div className="absolute top-0 bottom-0 bg-gradient-to-r from-crimson/30 via-crimson to-crimson/30 rounded-full" style={{
                          left: data.salary_max > 0 ? `${(data.salary_min / data.salary_max) * 100 * 0.9}%` : "0%",
                          right: "5%",
                        }} />
                        {/* Median marker */}
                        {data.salary_median > 0 && data.salary_max > 0 && (
                          <div className="absolute top-0 bottom-0 w-0.5 bg-white" style={{
                            left: `${(data.salary_median / data.salary_max) * 95}%`,
                          }}>
                            <div className="absolute -top-6 -translate-x-1/2 text-xs text-gray-400 whitespace-nowrap">Median</div>
                          </div>
                        )}
                      </div>
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>{formatSalary(data.salary_min, data.currency)}</span>
                        <span>{formatSalary(data.salary_max, data.currency)}</span>
                      </div>
                    </div>

                    {/* By experience level */}
                    {data.by_experience?.length > 0 && (
                      <div className="mt-4">
                        <h4 className="text-xs text-gray-400 mb-3 uppercase tracking-wider">By Experience Level</h4>
                        <div className="space-y-3">
                          {data.by_experience.map((exp) => (
                            <div key={exp.range} className="flex items-center gap-3">
                              <span className="text-xs text-gray-400 w-24 flex-shrink-0">{exp.range}</span>
                              <div className="flex-1 h-6 bg-[#222] rounded relative overflow-hidden">
                                <div
                                  className="absolute top-0 bottom-0 bg-crimson/60 rounded"
                                  style={{
                                    left: data.salary_max > 0 ? `${(exp.min / data.salary_max) * 100}%` : "0%",
                                    width: data.salary_max > 0 ? `${((exp.max - exp.min) / data.salary_max) * 100}%` : "0%",
                                  }}
                                />
                              </div>
                              <span className="text-xs text-gray-400 w-28 flex-shrink-0 text-right">
                                {formatSalary(exp.median, data.currency)}
                              </span>
                              <span className="text-xs text-gray-600 w-8 text-right">({exp.count})</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </motion.div>
          )}

          {/* Empty state */}
          {!data && !loading && (
            <div className="text-center py-16 text-gray-500">
              <TrendingUp className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="text-sm">Search for a job title to see salary data</p>
              <p className="text-xs text-gray-600 mt-1">Data from community contributions and job postings</p>
            </div>
          )}
          </>
          )}
        </div>
      </main>
    </div>
  );
};

export default SalaryResearch;
